import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircleRounded, ErrorRounded } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import { useInviteUserByClientMutation } from '@api/clientUserManagement';
import { RootState } from '@stores/store';
import { ROLE_DISPLAY_LABELS } from '../../constants';
import { getEmailDomain } from '../../utils';

type InviteStatus = 'pending' | 'success' | 'error';

export type InviteEmailResults = {
  successEmails: string[];
  failedEmails: string[];
};

const dialogPaperSx = {
  borderRadius: '28px',
  maxWidth: '520px',
};

interface InviteStatusModalProps {
  open: boolean;
  clientCode: string;
  inviteeEmails: string[];
  selectedRole: CLIENT_USER_ROLE;
  selectedDashboardIds: string[];
  onClose: (results: InviteEmailResults) => void;
}

const InviteStatusModal: FC<InviteStatusModalProps> = ({
  open,
  clientCode,
  inviteeEmails,
  selectedRole,
  selectedDashboardIds,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentUserEmail = useSelector((state: RootState) => state.UserData.user.email);
  const currentUserDomain = getEmailDomain(currentUserEmail);

  const [inviteUserByClient] = useInviteUserByClientMutation();
  const [inviteStatuses, setInviteStatuses] = useState<Record<string, InviteStatus>>({});

  const uniqueInviteeEmails = useMemo(() => [...new Set(inviteeEmails)], [inviteeEmails]);

  const inviteSingleUser = useCallback(
    async (email: string) => {
      setInviteStatuses((prev) => ({ ...prev, [email]: 'pending' }));
      const isRestrictedMember = selectedRole === CLIENT_USER_ROLE.MEMBER;
      try {
        await inviteUserByClient({
          clientCode,
          email,
          username: email,
          companyRole: selectedRole,
          ...(isRestrictedMember && { submoduleIds: selectedDashboardIds }),
        }).unwrap();
        setInviteStatuses((prev) => ({ ...prev, [email]: 'success' }));
      } catch (error) {
        console.error('Error while inviting user:', error);
        setInviteStatuses((prev) => ({ ...prev, [email]: 'error' }));
      }
    },
    [inviteUserByClient, clientCode, selectedRole, selectedDashboardIds],
  );

  useEffect(() => {
    if (!open) return;

    const initialStatuses = Object.fromEntries(
      uniqueInviteeEmails.map((email) => [email, 'pending' as InviteStatus]),
    );
    setInviteStatuses(initialStatuses);

    let isCancelled = false;

    const inviteSequentially = async () => {
      for (const email of uniqueInviteeEmails) {
        if (isCancelled) return;
        await inviteSingleUser(email);
      }
    };

    inviteSequentially();

    return () => {
      isCancelled = true;
    };
  }, [open, uniqueInviteeEmails, inviteSingleUser]);

  const handleClose = () => {
    const successEmails = uniqueInviteeEmails.filter(
      (email) => inviteStatuses[email] === 'success',
    );
    const failedEmails = uniqueInviteeEmails.filter((email) => inviteStatuses[email] !== 'success');
    onClose({ successEmails, failedEmails });
  };

  const renderInviteStatus = (email: string) => {
    const status = inviteStatuses[email] || 'pending';

    if (status === 'pending') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'none',
            border: '0.5px solid rgb(221, 221, 221)',
            cursor: 'pointer',
            borderRadius: '0.5rem',
            padding: '0.25rem 0.5rem',
          }}
        >
          <CircularProgress size={16} sx={{ color: '#2F736E' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Sending...</Typography>
        </Box>
      );
    }

    if (status === 'success') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <CheckCircleRounded sx={{ color: '#2F736E', fontSize: '1rem' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Sent</Typography>
        </Box>
      );
    }

    if (status === 'error') {
      return (
        <Box
          component="button"
          onClick={() => inviteSingleUser(email)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'none',
            border: '0.5px solid rgb(221, 221, 221)',
            cursor: 'pointer',
            borderRadius: '0.5rem',
            padding: '0.25rem 0.5rem',
          }}
        >
          <ErrorRounded sx={{ color: '#E07B00', fontSize: '1rem' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Retry</Typography>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      aria-labelledby="invite-success-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          ...dialogPaperSx,
          width: isMobile ? 'min(90vw, 400px)' : '520px',
        },
      }}
    >
      <DialogTitle
        id="invite-success-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          py: '1.5rem',
        }}
      >
        <Typography
          component="span"
          sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: '2rem', color: '#162936' }}
        >
          {`Sending ${uniqueInviteeEmails.length} ${uniqueInviteeEmails.length > 1 ? 'invites' : 'invite'}`}
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ p: 0 }}>
          <CloseOutlinedIcon sx={{ fontSize: '1.5rem', color: '#162936' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          {uniqueInviteeEmails.map((email) => {
            const isExternal = getEmailDomain(email) !== currentUserDomain;
            return (
              <Box
                key={email}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                }}
              >
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0 }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      color: '#1B1C17',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {email}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: '#6C6C6C' }}>
                      {ROLE_DISPLAY_LABELS[selectedRole]} &bull; Expires in 30 days
                    </Typography>
                    {isExternal && (
                      <Typography
                        sx={{
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          color: '#D97706',
                          backgroundColor: '#FEF3C7',
                          borderRadius: '0.75rem',
                          px: '0.5rem',
                          py: '0.125rem',
                          lineHeight: '1.25rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        External Member
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ flexShrink: 0 }}>{renderInviteStatus(email)}</Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InviteStatusModal;
